import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Loader2, PencilLine } from "lucide-react";
import { AdminEligibility } from "@/lib/api";
import { CreditDecisionForm } from "@/components/admin/CreditDecisionForm";

const FIELD_LABELS = {
  verified_monthly_income: "Verified Monthly Income",
  approved_amount: "Approved / Recommended Amount",
  credit_status: "Credit Status",
  reviewer_comment: "Reviewer Comment",
};
const INR = (v) => (v == null || v === "" ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);
const fmt = (f, v) => (v == null ? "—" : ["verified_monthly_income", "approved_amount"].includes(f) ? INR(v) : String(v));

const Badge = ({ children, tone = "gold" }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase border ${
    tone === "gold" ? "border-[#D4AF37]/60 text-[#F0D788] bg-[#D4AF37]/10" : "border-white/20 text-white/70"}`}>{children}</span>
);

/** Controlled manual overrides — system value preserved, override audited (old/new/reason/user/time). */
export const CreditReviewPanel = ({ check, onSaved }) => {
  const [data, setData] = useState(null);
  const [decision, setDecision] = useState(check.credit_decision || null);
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try { setData(await AdminEligibility.review(check.id)); } catch (e) { setErr(e?.response?.data?.detail || "Unable to load review"); }
  }, [check.id]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (f) => { setEditing(f); setValue(""); setReason(""); setEvidence(""); setErr(""); };
  const save = async () => {
    if (reason.trim().length < 10) { setErr("A meaningful reason (at least 10 characters) is mandatory."); return; }
    setBusy(true); setErr("");
    try {
      await AdminEligibility.saveOverride(check.id, { field: editing, new_value: value, reason: reason.trim(), evidence_reference: evidence || undefined });
      setEditing(null); await load(); onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail; setErr(typeof d === "string" ? d : d?.[0]?.msg || "Save failed");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="mt-3 text-xs text-white/50">{err || "Loading credit review…"}</div>;
  const sys = data.system_values || {};
  const ov = data.overrides || {};

  return (
    <>
    <div data-testid="credit-review-panel" className="mt-3 rounded-xl border border-[#D4AF37]/25 bg-black/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] inline-flex items-center gap-1.5">
          <ShieldCheck size={12} /> Credit Review · Manual Override {data.latest_version_no ? `· vs assessment v${data.latest_version_no}` : ""}
        </div>
        {!data.assessment_available && <span className="text-[10px] text-amber-300">Run Preview Assessment / Generate once to populate system values</span>}
      </div>

      <div className="mt-2 grid gap-2">
        {Object.keys(FIELD_LABELS).map((f) => {
          const o = ov[f];
          return (
            <div key={f} data-testid={`override-row-${f}`} className={`rounded-lg border p-2.5 grid grid-cols-1 min-[400px]:grid-cols-[1fr_auto] gap-2 ${o ? "border-[#D4AF37]/45 bg-[#D4AF37]/[0.06]" : "border-white/10"}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-xs text-white/85 font-medium">{FIELD_LABELS[f]} {o && <Badge>Overridden</Badge>}</div>
                <div className="mt-1 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                  <div><span className="text-white/45 uppercase text-[9px] tracking-wider">System value</span><div className="text-white/75 wrap-anywhere">{f === "verified_monthly_income" && sys[f] == null ? "Not established from documents" : fmt(f, sys[f])}{f === "verified_monthly_income" && sys.verified_monthly_income_status ? <span className="text-white/45"> · {sys.verified_monthly_income_status}</span> : null}</div></div>
                  <div><span className="text-white/45 uppercase text-[9px] tracking-wider">Admin override</span><div className={`wrap-anywhere ${o ? "text-[#F0D788] font-semibold" : "text-white/35"}`}>{o ? fmt(f, o.new_value) : "—"}</div></div>
                </div>
                {o && <div className="mt-1 text-[10px] text-white/55 wrap-anywhere">{o.reason} · {o.admin_name} · {new Date(o.created_at).toLocaleString()}</div>}
              </div>
              {data.can_override && (
                <button data-testid={`override-edit-${f}`} onClick={() => startEdit(f)} className="self-start inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 text-[11px] text-white/80 hover:border-[#D4AF37] min-h-[36px]">
                  <PencilLine size={11} /> {o ? "Change" : "Override"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <div data-testid="override-form" className="mt-3 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-3 grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 text-xs text-white/85 font-semibold">{FIELD_LABELS[editing]}</div>
          <div className="text-xs"><div className="text-white/50 text-[10px] uppercase">Current / old value</div><div className="text-white/85 mt-1">{fmt(editing, ov[editing]?.new_value ?? sys[editing])}</div></div>
          <div className="text-xs">
            <label className="text-white/50 text-[10px] uppercase">Proposed new value</label>
            {editing === "credit_status" ? (
              <select data-testid="override-value" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1 w-full rounded-lg bg-[#04102B] border border-white/15 px-3 py-2 text-white text-sm">
                <option value="">Select status…</option>
                {data.credit_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : editing === "reviewer_comment" ? (
              <textarea data-testid="override-value" value={value} onChange={(e) => setValue(e.target.value)} rows={3} className="mt-1 w-full rounded-lg bg-[#04102B] border border-white/15 px-3 py-2 text-white text-sm" />
            ) : (
              <input data-testid="override-value" type="number" min="1" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1 w-full rounded-lg bg-[#04102B] border border-white/15 px-3 py-2 text-white text-sm" placeholder="Amount in ₹" />
            )}
          </div>
          <div className="text-xs sm:col-span-2">
            <label className="text-white/50 text-[10px] uppercase">Reason for change (mandatory)</label>
            <textarea data-testid="override-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1 w-full rounded-lg bg-[#04102B] border border-white/15 px-3 py-2 text-white text-sm" placeholder="e.g. Salary slips Apr–Jun verified against SBI statement credits" />
          </div>
          {editing === "verified_monthly_income" && (
            <div className="text-xs sm:col-span-2">
              <label className="text-white/50 text-[10px] uppercase">Supporting evidence reference</label>
              <input data-testid="override-evidence" value={evidence} onChange={(e) => setEvidence(e.target.value)} className="mt-1 w-full rounded-lg bg-[#04102B] border border-white/15 px-3 py-2 text-white text-sm" placeholder="Document name / page" />
            </div>
          )}
          {err && <div data-testid="override-error" className="sm:col-span-2 text-xs text-rose-300">{String(err)}</div>}
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            <button data-testid="override-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/60 text-[#F0D788] text-xs font-semibold disabled:opacity-50 min-h-[40px]">
              {busy && <Loader2 size={12} className="animate-spin" />} Save Override (audited)
            </button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full border border-white/15 text-white/75 text-xs min-h-[40px]">Cancel</button>
          </div>
        </div>
      )}
      {!editing && err && <div className="mt-2 text-xs text-rose-300">{String(err)}</div>}

      {data.requires_confirmation && (
        <div data-testid="review-gate-note" className="mt-3 text-[11px] text-amber-200/90">
          Overrides present — final PDF generation will ask you to confirm: “Generate Final Eligibility Letter Using These Reviewed Values”.
        </div>
      )}

      <button data-testid="override-history-toggle" onClick={() => setShowHistory((s) => !s)} className="mt-3 text-[11px] text-white/60 hover:text-[#F0D788] underline underline-offset-2">
        {showHistory ? "Hide" : "Show"} override audit history ({data.history?.length || 0})
      </button>
      {showHistory && (
        <div data-testid="override-history" className="mt-2 space-y-1.5">
          {(data.history || []).map((h) => (
            <div key={h.id} className="text-[11px] text-white/70 border-l-2 border-[#D4AF37]/40 pl-2 wrap-anywhere">
              <span className="text-white/90">{FIELD_LABELS[h.field] || h.field}</span>: {fmt(h.field, h.old_value)} → <span className="text-[#F0D788]">{fmt(h.field, h.new_value)}</span>
              <div className="text-white/45">{h.reason} · {h.admin_name} · {new Date(h.created_at).toLocaleString()} · v{h.assessment_version_no ?? "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
    <CreditDecisionForm checkId={check.id} current={decision} canDecide={data.can_override} onSaved={(d) => { setDecision(d); onSaved?.(); }} />
    </>
  );
};
